import {ConfigObjectType, RISKINESS_COLORS} from '@wallaby-ui/core';
import {useFormatters, useMedicationAtcs} from '@wallaby-ui/document';
import lodash from 'lodash';
import {useCallback, useMemo} from 'react';

import {CHART_COLORS, InterventionsDepartmentReportDto, ReportType} from './departmenttypes';
import {MedicationAtc} from '../../hospitalizations_detail/HospitalizationPhRecommendationPanelV2';

type ChartPropType = Exclude<keyof InterventionsDepartmentReportDto, 'basicMeasures'>;

export const reportTypeToReportChartPropMap: Record<ReportType, ChartPropType> = {
  CAUSES_OF_PROBLEMS: 'causeStatistics',
  DRPS_PER_ATC: 'atcHierarchyStatistics',
  PLANNED_INTERVENTIONS: 'plannedInterventionStatistics',
  RISK_CATEGORY: 'riskinessStatistics',
  TYPES_OF_PROBLEMS: 'typeOfProblemStatistics',
};

export const reportTypeToEnumMap: Record<Exclude<ReportType, 'DRPS_PER_ATC'>, string> = {
  CAUSES_OF_PROBLEMS: 'DrpCause',
  PLANNED_INTERVENTIONS: 'DrpPlannedIntervention',
  RISK_CATEGORY: 'Riskiness',
  TYPES_OF_PROBLEMS: 'DrpType',
};

export type CategoryType = {
  index: number;
  category: string;
  label: string;
  color: string;
  fillColor: string;
};

const RISK_CATEGORIES_ORDER: string[] = ['HIGH', 'MEDIUM', 'LOW'];

export function useDepartmentReportChartCategories(
  reportType: ReportType,
  data: InterventionsDepartmentReportDto | undefined,
  config: ConfigObjectType,
  categoriesOrder: string[] | undefined
) {
  const {t, tEnum} = useFormatters();
  const {data: allMedicationAtcs} = useMedicationAtcs<MedicationAtc>(config);

  const getCategoryColor = useCallback(
    (category: string, index: number) => {
      if (category == null || category === 'undefined') return CHART_COLORS[CHART_COLORS.length - 1];
      if (reportType === ReportType.RISK_CATEGORY) {
        return RISKINESS_COLORS[category]?.hexDarker;
      }
      return CHART_COLORS[index];
    },
    [reportType]
  );

  const getCategoryLabel = useCallback(
    (category: string | null | undefined) => {
      if (category == null || category === 'undefined') return t('department.report.category.other');
      if (reportType === ReportType.DRPS_PER_ATC) {
        return (allMedicationAtcs ?? []).find((value) => `${value.id}` === category)?.label ?? category;
      }
      return tEnum(reportTypeToEnumMap[reportType], category) ?? category;
    },
    [reportType, tEnum, t, allMedicationAtcs]
  );

  return useMemo(() => {
    const statisticsPath = reportTypeToReportChartPropMap[reportType];
    const statistics = data?.[statisticsPath];

    const categories = lodash((statistics?.charts ?? []).map((item) => (lodash.isNil(item.category) ? 'undefined' : String(item.category))))
      .uniq()
      .value();

    const sortedCategories = lodash.sortBy(categories, (category) => {
      const categoryIndex =
        reportType === ReportType.RISK_CATEGORY ? RISK_CATEGORIES_ORDER.indexOf(category) : (categoriesOrder ?? []).indexOf(category);
      return categoryIndex !== -1 ? categoryIndex : Number.MAX_SAFE_INTEGER;
    });

    const categoryTypes: CategoryType[] = sortedCategories.map((category, index) => ({
      index,
      category,
      label: getCategoryLabel(category),
      color: getCategoryColor(category, index),
      fillColor: `${getCategoryColor(category, index)}80`,
    }));

    const categoriesRecord = lodash.keyBy(categoryTypes, 'category') as Record<string, CategoryType>;

    const getCategory = (category: string): CategoryType | undefined => {
      return lodash.get(categoriesRecord, category);
    };

    const getCategories = (): CategoryType[] => {
      return lodash.orderBy(categoryTypes, 'index');
    };

    return {
      categoriesRecord,
      getCategory,
      getCategories,
      getCategoryLabel,
    };
  }, [reportType, data, getCategoryLabel, categoriesOrder, getCategoryColor]);
}
